import { useCallback, useMemo, useState } from 'react'
import { sortTargetKeys } from './lib/targetKeys'
import type { TargetRef } from './lib/collectTargets'

/** Строка списка: баннер, маскоты целиком или один урок. */
export type RowId = 'banner' | 'mascots' | `lesson:${number}`

/**
 * Выбор строк для ручной дорисовки и сборка плана целей. Строки сообщают
 * свои цели сами (`setRowTargets`) — агрегирующего хука по всем урокам нет
 * намеренно, хуки нельзя звать в цикле по урокам.
 *
 * `rowIds` — ЖИВЫЕ строки списка. Исчезнувший урок размонтирует свою строку
 * вместе с шансом обнулить цели, и без пересечения с живым списком его ключи
 * уходили бы в план и в цену вечно: ровно тот случай, ради которого
 * `useRefreshTargets` перечитывает деталь курса после `unknown_image_targets`.
 */
export const useTargetSelection = (rowIds: RowId[]) => {
  const [selected, setSelected] = useState<Set<RowId>>(new Set())
  const [rows, setRows] = useState<Partial<Record<RowId, TargetRef[]>>>({})
  const [overwrite, setOverwrite] = useState(false)
  // Подпись, а не ссылка: вызывающий волен собирать массив строк на каждый
  // рендер, а план от новой ссылки пересчитываться не должен — он уезжает
  // наверх эффектом и гонял бы родителя по кругу.
  const liveIds = rowIds.join('~')

  const toggle = useCallback((id: RowId) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Строка сама сообщает свои цели, когда её блоки доехали: агрегирующего
  // хука нет намеренно — хуки нельзя звать в цикле по урокам.
  const setRowTargets = useCallback((id: RowId, targets: TargetRef[]) => {
    setRows((prev) => (prev[id] === targets ? prev : { ...prev, [id]: targets }))
  }, [])

  const plan = useMemo(() => {
    const live = new Set<string>(liveIds.split('~'))
    const keys = [...selected]
      .filter((id) => live.has(id))
      .flatMap((id) => rows[id] ?? [])
      // Дефолт совпадает с авто-режимом: платим только за пустые места.
      // Перерисовка — осознанное действие автора, а не побочный эффект выбора.
      .filter((t) => overwrite || !t.hasImage)
      .map((t) => t.key)
    return sortTargetKeys(keys)
  }, [selected, rows, overwrite, liveIds])

  return { selected, toggle, setRowTargets, overwrite, setOverwrite, plan, planSize: plan.length }
}
